/**
 * SonicFi/CyberTan RSW adopt preview -- server-state hooks (OP#1838, design
 * docs/design/sonicfi-switch-support.md). READ-ONLY: nothing here pushes a configuration.
 *
 *   GET  /devices/{serial}/switch/adopt-preview                       preview from the last read
 *   POST /devices/{serial}/switch/adopt-preview {read_device: true}   read the switch now: one
 *        relayed SSH login, fixed read-only commands, max once a minute per switch, audited.
 *        Baseline floor site_operator (it is a device login, even a read-only one).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { qk } from "./queryKeys";
import { useSession } from "@/stores/session";
import type { AdoptPreview } from "@/lib/sonicfiAdopt";

export function useSonicfiAdoptPreview(serial: string | undefined, enabled = true) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.sonicfiAdopt(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<AdoptPreview>(`/devices/${serial}/switch/adopt-preview`, undefined, signal),
    enabled: !!serial && enabled,
    staleTime: 60_000,
  });
}

/** POST {read_device: true}: read the switch now and return the fresh preview. */
export function useSonicfiAdoptRead(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: () =>
      api.post<AdoptPreview>(`/devices/${serial}/switch/adopt-preview`, { read_device: true }),
    onSuccess: (data) => {
      qc.setQueryData(qk.devices.sonicfiAdopt(orgId, serial ?? ""), data);
    },
  });
}
